import {
  type UndefinedInitialDataOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import {
  type TokensSearchQueryProps,
  tokenAcrossChainsQuery,
  tokensSearchQuery,
} from '@/src/queries/keys/tokens'
import type { TokenAcrossChains } from '@/src/services/tokens'

/**
 * This function is a wrapper around the `useInfiniteQuery` hook from `@tanstack/react-query`.
 * It uses the `tokensSearchQuery` function to fetch paginated token data based on the provided search parameters.
 *
 * @param props - The search parameters for querying tokens. Optional.
 * @returns The result of the infinite query, including data, status, and pagination controls.
 */
export function useSupportedTokens(props: TokensSearchQueryProps = {}) {
  return useInfiniteQuery(tokensSearchQuery(props))
}

export const useTokenAcrossChains = (
  chain?: string | null,
  denom?: string | null,
  options?: Partial<
    UndefinedInitialDataOptions<
      TokenAcrossChains,
      Error,
      TokenAcrossChains,
      (string | null | undefined)[]
    >
  >,
) =>
  useQuery({
    ...tokenAcrossChainsQuery(chain, denom),
    ...options,
  })

export function useGlobalSearch({
  assetQuery,
  enabled,
}: {
  assetQuery: string
  enabled: boolean
}) {
  return useInfiniteQuery({
    ...tokensSearchQuery({
      query: assetQuery,
      withPrice: true,
      perPage: 20,
    }),
    enabled: enabled,
    select: (data) =>
      data.pages.flatMap((page) =>
        page.assets.map((asset) => {
          return {
            icon: asset.variant_logo_img_url || '',
            name: asset.variant_display_name || '',
            symbol: asset.ticker,
            denom: asset.denoms_by_chain[0].denom,
            price: asset.price_usd,
            chainId: asset.denoms_by_chain[0].chain_id,
          }
        }),
      ),
  })
}
